import * as yup from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";

// Custom phone validation that allows 555 area codes for US numbers
export const isValidPhoneWithFictionalAreaCodes = (value: string): boolean => {
  if (!value) return false;

  // First check if it's a US +1 number with 555 area code using regex
  // Matches formats like: +1 555 555 5555, +15555555555, +1 (555) 555-5555, etc.
  const us555Pattern = /^\+1\s*\(?\s*555\s*\)?\s*\d{3}\s*[-\s]?\d{4}$/;
  if (us555Pattern.test(value)) {
    return true;
  }

  // For all other cases, use strict validation
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
};

export const loginSchema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email is required"),
    password: yup
      .string()
      .min(5, "Password must be at least 5 characters")
      .required("Password is required"),
  })
  .required();

export const registerSchema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email is required"),
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], "Passwords must match")
      .required("Confirm Password is required"),
  })
  .required();

export const passwordSchema = yup
  .object({
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
        "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character"
      )
      .required("New password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], "Passwords must match")
      .required("Confirm new password is required"),
  })
  .required();

export const resetPasswordSchema = yup
  .object({
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Email is required"),
  })
  .required();

export const profileSchema = yup.object().shape({
  first_name: yup
    .string()
    .required("First Name is required")
    .matches(
      /^([A-ZÀ-ÿ][-,a-z. ']+[ ]*)+$/,
      "First Name must start with a capital letter and contain valid characters"
    ),
  last_name: yup
    .string()
    .required("Last Name is required")
    .matches(
      /^([A-ZÀ-ÿ][-,a-z. ']+[ ]*)+$/,
      "Last Name must start with a capital letter and contain valid characters"
    ),

  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),

  current_password: yup
    .string()
    .test("conditional-required", "Old Password is required", function (value) {
      const { new_password, confirmPassword } = this.parent;
      if (new_password || confirmPassword) {
        return !!value;
      }
      return true;
    }),
  new_password: yup
    .string()
    .test("conditional-required", "New password is required", function (value) {
      const { current_password, confirmPassword } = this.parent;
      if (current_password || confirmPassword) {
        return !!value;
      }
      return true;
    })
    .test(
      "min-length",
      "New Password must be at least 8 characters",
      function (value) {
        const { current_password, confirmPassword } = this.parent;
        if ((current_password || confirmPassword) && value) {
          return value.length >= 8;
        }
        return true;
      }
    ),

  confirmPassword: yup
    .string()
    .test(
      "conditional-required",
      "Please confirm your password",
      function (value) {
        const { current_password, new_password } = this.parent;
        if (current_password || new_password) {
          return !!value;
        }
        return true;
      }
    )
    .test("passwords-match", "Passwords must match", function (value) {
      const { current_password, new_password } = this.parent;
      if ((current_password || new_password) && value) {
        return value === new_password;
      }
      return true;
    }),
  pincode: yup
    .string()
    .optional()
    .test(
      "is-valid-zip",
      "Zip code must be exactly 5 digits",
      function (value) {
        // If no value provided, it's valid (field is optional)
        if (!value || value.trim() === "") return true;
        // If value provided, check if it's 5 digits
        return /^[0-9]{5}$/.test(value);
      }
    ),
  address: yup.string().optional(),
  address_two: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  is_2fa_required: yup.boolean(),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});

export const administratorBookingSchema = yup.object().shape({
  maximum_players_in_total: yup
    .number()
    .required("Maximum number of bays is required")
    .min(1, "Must be at least 1 bay")
    .typeError("Must be a number"),
  no_show: yup.string().required("No Show is required"),
  advanced_booking_days: yup
    .number()
    .required("Advanced booking days are required")
    .min(2, "Must be at least 2 day")
    .max(365, "Max can be 365 days")
    .typeError("Must be a number"),

  maximum_players_per_bay: yup
    .number()
    .required("Maximum players per bay is required")
    .min(1, "Must be at least 1 player")
    .typeError("Must be a number"),
  minimum_booking_duration: yup
    .string()
    .required("Minimum booking duration is required"),
  customer_cancellation: yup
    .string()
    .required("Customer cancellation  is required"),

  cancellation_grace_period_day: yup
    .string()
    .required("Cancellation grace period is required"),

  cancellation_grace_period_duration: yup
    .number()
    .required("Cancellation grace duration is required")
    .min(1, "Must be at least 1")
    .typeError("Must be a number"),

  maximum_booking_duration: yup
    .string()
    .required("Maximum booking duration is required"),

  bay_selection_algorithm: yup
    .string()
    .required("Bay Selection algorithm is required"),

  booking_timeouts: yup.string().required("Booking timeouts are required"),
  send_email_notifications: yup.boolean(),
  send_text_messages: yup.boolean(),
  apply_same_settings_for_employees: yup.boolean(),
  apply_same_settings_for_customers: yup.boolean(),

  past_hour_grace_period: yup
    .string()
    .required("Past hour grace period is required"),
  max_number_of_bookings_allowed: yup
    .string()
    .required("Maximum number of bookings allowed is required"),
  guest_fees: yup.string().required("Guest fees is required"),
});

export const employeeBookingSchema = yup.object().shape({
  employee_maximum_players_in_total: yup
    .number()
    .min(1, "Must be at least 1 bay")
    .typeError("Must be a number"),
  employee_maximum_players_per_bay: yup
    .number()
    .min(1, "Must be at least 1 player")
    .typeError("Must be a number"),
  employee_minimum_booking_duration: yup.string(),
  employee_maximum_booking_duration: yup.string(),
  employee_customer_cancellation: yup.string(),
  employee_cancellation_grace_period_duration: yup
    .number()
    .min(1, "Must be at least 1")
    .typeError("Must be a number"),
  employee_advanced_booking_days: yup.number(),
  employee_no_show: yup.string(),
  employee_booking_timeouts: yup.string(),
  employee_bay_selection_algorithm: yup.string(),
  employee_cancellation_grace_period_day: yup.string(),
  employee_past_hour_grace_period: yup.string(),
  employee_max_number_of_bookings_allowed: yup.string(),
  employee_send_text_messages: yup.boolean(),
  employee_send_email_notifications: yup.boolean(),
});

export const customerBookingSchema = yup.object().shape({
  customer_maximum_players_in_total: yup
    .number()
    .min(1, "Must be at least 1 bay")
    .typeError("Must be a number"),
  customer_maximum_players_per_bay: yup
    .number()
    .min(1, "Must be at least 1 player")
    .typeError("Must be a number"),
  customer_minimum_booking_duration: yup.string(),
  customer_maximum_booking_duration: yup.string(),
  customer_customer_cancellation: yup.string(),
  customer_cancellation_grace_period_duration: yup
    .number()
    .min(1, "Must be at least 1")
    .typeError("Must be a number"),
  customer_advanced_booking_days: yup.number(),
  customer_no_show: yup.string(),
  customer_booking_timeouts: yup.string(),
  customer_past_hour_grace_period: yup.string(),
  customer_max_number_of_bookings_allowed: yup.string(),
  customer_cancellation_grace_period_day: yup.string(),
  customer_bay_selection_algorithm: yup.string(),
  booking_notices: yup.array().of(yup.object().shape({ notice: yup.string() })),
  customer_send_text_messages: yup.boolean(),
  customer_send_email_notifications: yup.boolean(),
});

export const venueSchema = yup.object().shape({
  name: yup.string().required("Venue name is required"),
  number_of_simulators: yup
    .number()
    .required("Number of simulators is required"),
  country: yup.string().optional(),
  zip_code: yup.string().optional(),
  city: yup.string().optional(),
  address_line_1: yup.string().optional(),
  address_line_2: yup.string().optional(),
  state: yup.string().optional(),
  // Sidebar section visibility
  has_golf_course: yup.boolean().optional(),
  has_bays: yup.boolean().optional(),
});

export const customerSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Customer Name is required")
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Customer Name must only contain letters and spaces, and cannot start or end with a space"
    ),
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Invalid email address")
    .max(30, "Email must be at most 30 characters"),
  dob: yup
    .object({
      day: yup.string(),
      month: yup.string(),
      year: yup.string(),
    })
    .required()
    .test("dob-validation", "Please enter complete date of birth", (value) => {
      if (!value || (!value.day && !value.month && !value.year)) return true;
      return Boolean(value.day && value.month && value.year);
    }),
  plan: yup.number().nullable(),
  plan_frequency: yup
    .string()
    .nullable()
    .when("plan", {
      is: (plan: number | null) => plan !== null && plan !== undefined,
      then: (schema) => schema.required("Frequency is required "),
    }),
  source: yup.string(),
  notes: yup.string(),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),
});

export const languageSchema = yup.object().shape({
  country: yup.string().required("Country is required"),
  currency: yup.string().required("Currency is required"),
  language: yup.string().required("Language is required"),
  timezone: yup.string().required("Timezone is required"),
  hour_format: yup.string().required("Hour format is required"),
  date_format: yup.string().required("Date format is required"),
  first_day_of_the_week: yup
    .string()
    .required("First day of the week is required"),
});

export const bookingSchema = yup.object({
  customer: yup.string().required("Customer is required").nullable(),
  status: yup.string().required("Status is required"),
  payment_method: yup
    .string()
    .required("Payment is required")
    .when("status", {
      is: "Pending",
      then: (schema) =>
        schema.oneOf(
          ["unpaid"],
          "Payment must be unpaid when status is pending"
        ),
    }),
  source: yup.string().required("Source is required"),
  notify_email: yup.boolean(),
  notes: yup.string(),
});
export const EventSchema = yup.object().shape({
  title: yup
    .string()
    .required("Event title is required.")
    .max(100, "Event title cannot exceed 100 characters."),
  number_of_attendees: yup
    .number()
    .required("Number of attendees is required.")
    .min(1, "At least one attendee is required.")
    .max(500, "Number of attendees cannot exceed 500."),
  event_type: yup.string().required("Event type is required."),
  // event_category: yup.number().required("Event category is required."),
  main_contact: yup.string().required("Contact name is required."),
  cost: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string or NaN
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return null;
      }
      // Handle NaN after number conversion
      return isNaN(value) ? null : value;
    })
    .nullable()
    .min(0, "Event cost cannot be negative.")
    .optional(),

  event_date: yup.string().required("Event date is required."),
  display_on_website_calendar: yup.boolean().optional(),
  duration: yup.number().optional(),
  notify_contacts: yup.boolean().optional(),

  event_image: yup
    .mixed<File | string>()
    .test("fileType", "Only files are allowed.", (value) => {
      if (value === undefined || value === null) return true;
      return value instanceof File || typeof value === "string";
    })
    .optional()
    .nullable(),

  notes: yup.string().nullable().optional(),

  // Recurring event fields
  is_recurring: yup.boolean().optional(),
  recurrence_frequency: yup
    .string()
    .when("is_recurring", {
      is: true,
      then: (schema) =>
        schema
          .required("Recurrence frequency is required for recurring events")
          .oneOf(
            ["daily", "weekly", "monthly"],
            "Recurrence frequency must be daily, weekly, or monthly"
          ),
      otherwise: (schema) => schema.optional(),
    }),
  recurrence_end_date: yup
    .string()
    .when("is_recurring", {
      is: true,
      then: (schema) =>
        schema
          .required("Recurrence end date is required for recurring events")
          .test(
            "is-after-start",
            "Recurrence end date must be after the event start date",
            function (value) {
              const { event_date } = this.parent;
              if (!value || !event_date) return true;
              return new Date(value) > new Date(event_date);
            }
          ),
      otherwise: (schema) => schema.optional(),
    }),
  recurrence_days_of_week: yup
    .array()
    .of(yup.number().min(0).max(6).required())
    .when(["is_recurring", "recurrence_frequency"], {
      is: (is_recurring: boolean, recurrence_frequency: string) =>
        is_recurring && recurrence_frequency === "weekly",
      then: (schema) =>
        schema
          .required("Days of week are required for weekly recurrence")
          .min(1, "At least one day must be selected for weekly recurrence")
          .test(
            "valid-days",
            "Days of week must contain integers between 0 (Monday) and 6 (Sunday)",
            (value) => {
              if (!value) return true;
              return value.every((day) => day !== undefined && day >= 0 && day <= 6);
            }
          ),
      otherwise: (schema) => schema.optional(),
    }),
  source: yup.string().optional(),
});
// export const EventSchema = yup.object().shape({
//   title: yup
//     .string()
//     .required("Event title is required.")
//     .max(100, "Event title cannot exceed 100 characters."),
//   number_of_attendees: yup
//     .number()
//     .required("Number of attendees is required.")
//     .min(1, "At least one attendee is required.")
//     .max(500, "Number of attendees cannot exceed 500."),
//   event_type: yup.string().required("Event type is required."),
//   event_category: yup.number().required("Event category is required."),
//   main_contact: yup.string().required("Contact name is required."),
//   cost: yup.number().optional().min(0, "Event cost cannot be negative."),
//   event_date: yup.string().required("Event date is required."),
//   display_on_website_calendar: yup.boolean().optional(),
//   duration: yup.number().optional(),
//   notify_contacts: yup.boolean().optional(),
//   event_image: yup.mixed().optional(),
// });

export const membershipSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  status: yup.string(),
  initiation_fee: yup
    .string()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      "Initiation fee must only contain numbers with up to two decimal places"
    ),
  daily_duration_limit: yup
    .string()
    .required("Daily duration limit is required"),
  advance_booking_days: yup
    .string()
    .matches(/^\d*$/, "Advance booking days must only contain numbers"),
  daily_guest_limit: yup.string(),
  peak_hour_discount: yup
    .string()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      "Peak hour discount must only contain numbers with up to two decimal places"
    ),
  holiday_booking_allowed: yup.string(),
  maximum_concurrent_bookings: yup
    .string()
    .required("Maximum concurrent bookings is required"),
  guest_fees: yup
    .string()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      "Guest fees must only contain numbers with up to two decimal places"
    ),
  addition_rules: yup.array().of(
    yup.object().shape({
      rule: yup.string(),
      is_active: yup.boolean(),
    })
  ),
  monthly: yup.boolean(),
  monthly_amount: yup.string().when("monthly", {
    is: true,
    then: (schema) => schema.required("Monthly amount is required"),
  }),
  quarterly: yup.boolean(),
  quarterly_amount: yup.string().when("quarterly", {
    is: true,
    then: (schema) => schema.required("Quarterly amount is required"),
  }),
  half_year: yup.boolean(),
  half_year_amount: yup.string().when("half_year", {
    is: true,
    then: (schema) => schema.required("Half year amount is required"),
  }),
  yearly: yup.boolean(),
  yearly_amount: yup.string().when("yearly", {
    is: true,
    then: (schema) => schema.required("Yearly amount is required"),
  }),
  display_on_website: yup.boolean(),
  max_bookings_per_period: yup.string(),
  // Membership Hours Seasonal Feature fields
  allow_bookings_outside_business_hours: yup.boolean().default(false),
  ignore_business_hours_validation: yup.boolean().default(false),
  auto_sync_start_time_with_business: yup.boolean().default(false),
  auto_sync_end_time_with_business: yup.boolean().default(false),
});

export const memberSchema = yup.object().shape({
  membership_plan: yup.number().required("Membership Plan is required"),
  billing_frequency: yup.string().required("Billing Frequency is required"),
  start_date: yup.string().nullable().notRequired(),
  end_date: yup
    .string()
    .nullable()
    .when("start_date", {
      is: (start_date: string | null | undefined) => !!start_date,
      then: (schema) => schema.required("End Date is required "),
      otherwise: (schema) => schema.nullable(),
    }),
  auto_renew: yup.boolean().nullable().notRequired(),
});
export const newMemberSchema = yup.object().shape({
  user: yup.number().required("Customer is required"),
  membership_plan: yup.number().required("Membership Plan is required"),
  billing_frequency: yup.string().required("Billing Frequency is required"),
  start_date: yup.string().nullable().notRequired(),
  end_date: yup
    .string()
    .nullable()
    .when("start_date", {
      is: (start_date: string | null | undefined) => !!start_date,
      then: (schema) => schema.required("End Date is required "),
      otherwise: (schema) => schema.nullable(),
    }),
  auto_renew: yup.boolean().nullable().notRequired(),
});

export const quickBookSchema = yup.object().shape({
  customerName: yup
    .string()
    .required("Customer Name is required")
    .min(3, "Customer Name must be at least 3 characters"),
  customerEmail: yup.string().email("Invalid email address"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),
  paid_booking: yup.boolean().required("Paid Booking is required"),
});

export const eventContactSchema = yup.object().shape({
  first_name: yup
    .string()
    .required("Player Name is required")
    .min(3, "Player Name must be at least 3 characters"),

  last_name: yup
    .string()
    .required("player Lastname is required")
    .min(3, "player Lastname must be at least 3 characters"),

  email: yup.string().email("Invalid email address"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),

  subscribed_events: yup.array(
    yup
      .object({
        event: yup.string(),
      })
      .optional()
  ),
});
export const editContactSchema = yup.object().shape({
  first_name: yup
    .string()
    .required("Player Name is required")
    .min(3, "Player Name must be at least 3 characters"),

  last_name: yup
    .string()
    .required("player Lastname is required")
    .min(3, "player Lastname must be at least 3 characters"),

  email: yup
    .string()
    .required("Email is required")
    .email("Invalid email address"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),

  dob: yup.string().nullable().typeError("Invalid date format"),
  source: yup.string(),
  notes: yup.mixed().optional(),
});

export const addUserSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().optional(),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Phone Number must be valid", (value) => {
      if (!value) return false;
      return isValidPhoneWithFictionalAreaCodes(value);
    }),
  country: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  address: yup.string().optional(),
  address_two: yup.string().optional(),
  zip_code: yup.string().optional(),
  is_2fa_required: yup.boolean(),
  is_active: yup.string().required("Status is required"),
  password: yup
    .string()
    .test(
      "password-required",
      "Password is required",
      function (value) {
        const isEdit = this.options.context?.isEdit;
        if (isEdit) return true; // Password not required in edit mode
        return !!value; // Password required in add mode
      }
    )
    .test(
      "password-min-length",
      "Password must be at least 8 characters",
      function (value) {
        const isEdit = this.options.context?.isEdit;
        if (isEdit) return true; // Skip validation in edit mode
        if (!value) return true; // Will be caught by required test
        return value.length >= 8;
      }
    ),
  confirm_password: yup
    .string()
    .test(
      "confirm-password-required",
      "Confirm Password is required",
      function (value) {
        const isEdit = this.options.context?.isEdit;
        if (isEdit) return true; // Not required in edit mode
        return !!value; // Required in add mode
      }
    )
    .test(
      "passwords-match",
      "Passwords must match",
      function (value) {
        const isEdit = this.options.context?.isEdit;
        if (isEdit) return true; // Skip validation in edit mode
        const { password } = this.parent;
        if (!value || !password) return true; // Will be caught by required test
        return value === password;
      }
    ),
  is_admin: yup.boolean().required("Role is required"),
  is_coach: yup.boolean().default(false),
});

export const eventTypeSchema = yup.object({
  eventTypes: yup
    .array(
      yup.object({
        name: yup
          .string()
          .required(
            "Don't leave this empty — please enter a value or delete this field."
          ),
        id: yup.number().optional(),
      })
    )
    .required(),
});

export const contactFormSubmissionSchema = yup.object({
  status: yup.string().required(),
  response_message: yup.string().optional(),
  send_copy: yup.boolean().optional(),
  additional_emails: yup
    .array()
    .of(
      yup.object({
        email: yup
          .string()
          .email("Invalid email address")
          .required(
            "Don't leave this empty — please enter a value or delete this field."
          ),
      })
    )
    .optional(),
});

export const notificationSchema = yup.object({
  notificationMessage: yup
    .string()
    .required("Notification message is required"),
  additional_emails: yup.array().of(
    yup.object({
      email: yup.string().email("Invalid email address").optional(),
    })
  ),
  send_copy_to_self: yup.boolean().optional(),
});

export const blockHourSchema = yup.object().shape({
  date: yup.string().required("Date is required"),
  blockStartTime: yup.string().required("Start time is required"),
  blockEndTime: yup.string().required("End time is required"),
  blockReason: yup.string().required("Reason is required"),
  otherBlockReason: yup.string().when("blockReason", {
    is: "other",
    then: (schema) => schema.required("Please specify the reason"),
    otherwise: (schema) => schema.optional(),
  }),
});

export const blockBaySchema = yup.object().shape({
  date: yup
    .mixed()
    .test("is-string-or-date-range", "Date is required", (value) => {
      if (
        (typeof value === "string" && value.trim() !== "") ||
        value instanceof Date
      ) {
        return true;
      }
      if (
        value &&
        typeof value === "object" &&
        (value as { from: Date; to: Date | null }).from instanceof Date &&
        (value as { from: Date; to: Date | null }).to instanceof Date
      ) {
        return true;
      }
      return false;
    })
    .required("Date is required"),
  blockReason: yup.string().required("Reason is required"),
  otherBlockReason: yup.string().when("blockReason", {
    is: "other",
    then: (schema) => schema.required("Please specify the reason"),
    otherwise: (schema) => schema.optional(),
  }),
});

export const bookingManagerNotificationSettingsSchema = yup.object().shape({
  // Booking-related
  new_booking: yup.boolean(),
  new_regular_booking: yup.boolean(),
  new_lesson_booking: yup.boolean(),
  booking_updated: yup.boolean(),
  booking_cancellation: yup.boolean(),
  // Membership-related
  new_membership: yup.boolean(),
  membership_updated: yup.boolean(),
  membership_cancellation: yup.boolean(),
  // Event-related
  new_event: yup.boolean(),
  // User management
  user_invitation: yup.boolean(),
  user_welcome: yup.boolean(),
  password_reset: yup.boolean(),
  // Transaction-related
  transactions: yup.boolean(),
  userToExclude: yup.string(),
  exclude_list: yup.array().of(
    yup.object().shape({
      id: yup.number().required(),
      name: yup.string().required(),
      email: yup.string().email(),
    })
  ),
});

export const addBaySchema = yup.object().shape({
  bay_number: yup.string().optional(),
  description: yup
    .string()
    .required("Description is required")
    .max(25, "Description must be less than 25 characters"),
  status: yup.string().required("Status is required"),
  general_bookings_only: yup
    .boolean()
    .required("General bookings only is required"),
  lessons_only: yup.boolean().required("Lessons only is required"),
  vip_bookings_only: yup.boolean().required("VIP bookings only is required"),
  club_fittings_available: yup
    .boolean()
    .required("Club fittings available is required"),
  out_of_service: yup.boolean().required(),
  reason: yup.string().optional(),
});

export const processRefundSchema = yup.object().shape({
  transactionId: yup.number().required("Transaction ID is required"),
  refundReason: yup.string().required("Refund reason is required"),
  notes: yup.string().optional(),
  bookings: yup
    .array()
    .of(
      yup.object().shape({
        bookingId: yup.number().required("Booking ID is required"),
        refundOption: yup.string().required("Refund option is required"),
        keepBookingActive: yup
          .boolean()
          .required("Keep booking active is required"),
        refundAmount: yup.string().when("refundOption", {
          is: "no_refund",
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required("Refund amount is required"),
        }),
      })
    )
    .required("At least one booking is required"),
});

export const InviteSchema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email is required"),
  })
  .required();
export const updateCoachSchema = yup.object({
  bio: yup.string().max(200, "Bio must be at most 200 characters").optional(),

  lesson_type_prices: yup
    .array(
      yup
        .object()
        .test(
          "all-numbers",
          "All values must be numbers",
          (obj) => obj && Object.values(obj).every((v) => typeof v === "number")
        )
    )
    .optional(),

  is_active: yup.boolean().optional(),

  profile_pic: yup
    .mixed<File>()
    .nullable()
    .optional()
    .test("fileType", "Only images are allowed", (file) => {
      if (!file) return true;
      return file instanceof File;
    }),
});

export const seasonalPricingSchema = yup.object().shape({
  name: yup.string().required("Season name is required").trim(),
});

export const templateSchema = yup.object().shape({
  template_name: yup
    .string()
    .required("Template name is required")
    .trim()
    .min(1, "Template name cannot be empty"),
});

// ============================================
// MARKETING POPUPS VALIDATION SCHEMAS
// ============================================

// Form field validation schema
export const popupFormFieldSchema = yup.object().shape({
  field_type: yup
    .string()
    .oneOf(
      ["text", "email", "phone", "textarea", "checkbox", "select", "radio"],
      "Invalid field type"
    )
    .required("Field type is required"),
  label: yup
    .string()
    .required("Field label is required")
    .max(100, "Label must be less than 100 characters"),
  placeholder: yup
    .string()
    .max(100, "Placeholder must be less than 100 characters")
    .optional(),
  is_required: yup.boolean().required("Required status is required"),
  order: yup.number().min(0, "Order must be positive").required("Order is required"),
  validation_rules: yup
    .object()
    .shape({
      min_length: yup.number().min(0).optional(),
      max_length: yup.number().min(0).optional(),
      pattern: yup.string().optional(),
      min: yup.number().optional(),
      max: yup.number().optional(),
    })
    .optional(),
  options: yup
    .array()
    .of(yup.string())
    .when("field_type", {
      is: (val: string) => ["select", "radio"].includes(val),
      then: (schema) =>
        schema.min(1, "At least one option is required for select/radio fields"),
      otherwise: (schema) => schema.optional(),
    }),
});

// Main popup configuration schema
export const popupConfigSchema = yup.object().shape({
  // Basic Info (Step 1)
  title: yup
    .string()
    .required("Popup title is required")
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must be less than 200 characters")
    .trim(),
  status: yup
    .string()
    .oneOf(["draft", "active", "paused", "ended"], "Invalid status")
    .optional(),
  priority: yup
    .number()
    .min(1, "Priority must be between 1 and 10")
    .max(10, "Priority must be between 1 and 10")
    .required("Priority is required"),
  start_datetime: yup.string().nullable().optional(),
  end_datetime: yup
    .string()
    .nullable()
    .optional()
    .test("is-after-start", "End date must be after start date", function (value) {
      const { start_datetime } = this.parent;
      if (!start_datetime || !value) return true;
      return new Date(value) > new Date(start_datetime);
    }),
  timezone: yup.string().optional(),

  // Content (Step 2)
  headline: yup
    .string()
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .test(
      "min-length-if-provided",
      "Headline must be at least 5 characters",
      (value) => !value || value.length >= 5
    )
    .max(200, "Headline must be less than 200 characters"),
  body_text: yup
    .string()
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .test(
      "min-length-if-provided",
      "Body text must be at least 10 characters",
      (value) => !value || value.length >= 10
    )
    .max(1000, "Body text must be less than 1000 characters"),
  cta_text: yup
    .string()
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .max(50, "CTA text must be less than 50 characters"),
  cta_url: yup
    .string()
    .url("Please enter a valid URL")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value)),

  // Design (Step 3)
  animation_type: yup
    .string()
    .oneOf(
      ["slide_left", "slide_right", "slide_up", "slide_down", "fade"],
      "Invalid animation type"
    )
    .required("Animation type is required"),
  position: yup
    .string()
    .oneOf(
      [
        "center",
        "top_left",
        "top_center",
        "top_right",
        "center_left",
        "center_right",
        "bottom_left",
        "bottom_center",
        "bottom_right",
      ],
      "Invalid position"
    )
    .required("Position is required"),
  display_delay: yup
    .number()
    .min(1, "Delay must be at least 1 second")
    .max(30, "Delay must be at most 30 seconds")
    .required("Display delay is required"),
  background_color: yup
    .string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Invalid hex color code")
    .required("Background color is required"),
  text_color: yup
    .string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Invalid hex color code")
    .required("Text color is required"),
  overlay_opacity: yup
    .number()
    .min(0, "Opacity must be between 0 and 1")
    .max(1, "Opacity must be between 0 and 1")
    .required("Overlay opacity is required"),

  // Targeting (Step 4)
  target_user_groups: yup
    .array()
    .of(yup.string().oneOf(["members", "guests", "all"], "Invalid user group"))
    .min(1, "At least one user group must be selected")
    .required("User groups are required"),
  target_devices: yup
    .array()
    .of(yup.string().oneOf(["mobile", "tablet", "desktop"], "Invalid device type"))
    .min(1, "At least one device type must be selected")
    .required("Device types are required"),
  target_urls: yup
    .array()
    .of(yup.string())
    .optional()
    .transform((value) => (value && value.length === 0 ? undefined : value)),
  target_new_visitors_only: yup.boolean().required("Target new visitors setting is required"),

  // Frequency (Step 5)
  frequency_type: yup
    .string()
    .oneOf(["once", "daily", "every_n_days", "session"], "Invalid frequency type")
    .required("Frequency type is required"),
  frequency_days: yup
    .number()
    .when("frequency_type", {
      is: "every_n_days",
      then: (schema) =>
        schema
          .min(1, "Frequency days must be at least 1")
          .max(365, "Frequency days must be less than 365")
          .required("Frequency days is required for 'every N days' type"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  max_impressions_per_user: yup
    .number()
    .min(1, "Max impressions must be at least 1")
    .optional()
    .nullable()
    .transform((value) => (value === "" || isNaN(value) ? null : value)),

  // Form Builder (Step 6)
  has_form: yup.boolean().required("Form setting is required"),
  require_gdpr_consent: yup.boolean().optional(),
  gdpr_consent_text: yup
    .string()
    .when("require_gdpr_consent", {
      is: true,
      then: (schema) =>
        schema
          .required("GDPR consent text is required when GDPR consent is enabled")
          .min(10, "GDPR text must be at least 10 characters"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  form_submit_message: yup
    .string()
    .when("has_form", {
      is: true,
      then: (schema) =>
        schema
          .required("Form submit message is required when form is enabled")
          .min(5, "Submit message must be at least 5 characters")
          .max(200, "Submit message must be less than 200 characters"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  form_fields: yup
    .array()
    .of(popupFormFieldSchema)
    .when("has_form", {
      is: true,
      then: (schema) =>
        schema.min(1, "At least one form field is required when form is enabled"),
      otherwise: (schema) => schema.optional(),
    }),

  // A/B Testing (Step 7)
  is_ab_test: yup.boolean().optional(),
  ab_variant_name: yup
    .string()
    .when("is_ab_test", {
      is: true,
      then: (schema) =>
        schema
          .required("Variant name is required for A/B testing")
          .max(50, "Variant name must be less than 50 characters"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  ab_test_group_id: yup
    .string()
    .when("is_ab_test", {
      is: true,
      then: (schema) =>
        schema
          .required("Test group ID is required for A/B testing")
          .max(100, "Test group ID must be less than 100 characters"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
});

// Lead filter schema
export const leadFilterSchema = yup.object().shape({
  popup_id: yup.string().optional(),
  start_date: yup.string().optional(),
  end_date: yup
    .string()
    .optional()
    .test("is-after-start", "End date must be after start date", function (value) {
      const { start_date } = this.parent;
      if (!start_date || !value) return true;
      return new Date(value) >= new Date(start_date);
    }),
  gdpr_consent: yup.boolean().optional(),
  device_type: yup
    .string()
    .oneOf(["mobile", "tablet", "desktop"], "Invalid device type")
    .optional(),
  country: yup.string().optional(),
  search: yup.string().optional(),
});

// Analytics filter schema
export const analyticsFilterSchema = yup.object().shape({
  popup_id: yup.string().optional(),
  start_date: yup.string().required("Start date is required"),
  end_date: yup
    .string()
    .required("End date is required")
    .test("is-after-start", "End date must be after start date", function (value) {
      const { start_date } = this.parent;
      if (!start_date || !value) return true;
      return new Date(value) >= new Date(start_date);
    }),
  device_type: yup
    .string()
    .oneOf(["mobile", "tablet", "desktop"], "Invalid device type")
    .optional(),
  country: yup.string().optional(),
});

// Booking Transfer schema
export const bookingTransferSchema = yup.object().shape({
  new_customer: yup
    .string()
    .required("Please select a customer to transfer the booking to")
    .nullable(),
  transfer_reason: yup.string().optional(),
  refund_to_card: yup.boolean().default(false),
  refund_amount: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string or null
      return originalValue === "" || originalValue === null ? undefined : value;
    })
    .when("refund_to_card", {
      is: true,
      then: (schema) =>
        schema
          .required("Refund amount is required when refunding to card")
          .min(0.01, "Refund amount must be greater than 0")
          .test(
            "max-refundable",
            "Refund amount cannot exceed maximum refundable amount",
            function () {
              // This will be validated on the backend, but we can add client-side validation
              // if max_refundable is passed as context
              return true;
            }
          ),
      otherwise: (schema) => schema.optional(),
    }),
  refund_reason: yup.string().optional(),
  notify_users: yup.boolean().default(true),
});

// Course Booking Settings Schema (Golf Tee Time Configuration)
export const courseBookingSettingsSchema = yup.object({
  interval_minutes: yup
    .number()
    .required("Tee time interval is required")
    .min(5, "Interval must be at least 5 minutes")
    .max(20, "Interval cannot exceed 20 minutes"),
  first_tee_time: yup.string().required("First tee time is required"),
  last_tee_time: yup.string().required("Last tee time is required"),
  max_players_per_slot: yup
    .number()
    .required("Maximum players is required")
    .min(1, "Must allow at least 1 player")
    .max(8, "Cannot exceed 8 players"),
  min_players_per_slot: yup
    .number()
    .required("Minimum players is required")
    .min(1, "Must require at least 1 player"),
  advance_booking_days: yup
    .number()
    .required("Advance booking days is required")
    .min(1, "Must allow at least 1 day advance booking"),
  member_advance_booking_days: yup
    .number()
    .required("Member advance booking days is required")
    .min(1, "Must allow at least 1 day advance booking for members"),
  cart_included: yup.boolean().default(false),
  walking_allowed: yup.boolean().default(true),
  allow_shotgun_start: yup.boolean().default(false),
});
